import React from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { urlEdizione } from '../links';
import NonTrovato from '../NonTrovato';
import TabellaOrdinabile from '../../../components/statistiche/TabellaOrdinabile';
import { LinkGiocatore, LinkSquadra } from '../../../components/statistiche/Link';
import { slugDaId } from './squadre';
import '../Statistiche.css';
import './Partita.css';

// Colonne del referto, condivise dalle due squadre: sono definite fuori dal componente perché
// TabellaOrdinabile tiene uno stato di ordinamento interno, e una funzione ricreata a ogni render
// di Partita farebbe scattare il remount di quello stato invece di limitarsi ad aggiornarlo.
const colonneReferto = [
  {
    chiave: 'giocatore',
    etichetta: 'Giocatore',
    testo: true,
    valore: (g) => `${g.cognome} ${g.nome}`,
    rende: (g) => <LinkGiocatore id={g.idGiocatore} nome={g.nome} cognome={g.cognome} />,
  },
  { chiave: 'mete', etichetta: 'M', etichettaLunga: 'Mete', valore: (g) => g.mete || 0 },
  { chiave: 'assist', etichetta: 'A', etichettaLunga: 'Assist', valore: (g) => g.assist || 0 },
  { chiave: 'pieni', etichetta: 'Pi', etichettaLunga: 'Pieni', valore: (g) => g.pieni || 0 },
  { chiave: 'vuoti', etichetta: 'V', etichettaLunga: 'Vuoti', valore: (g) => g.vuoti || 0 },
];

const Referto = ({ squadra, giocatori }) => (
  <section className="partita-referto">
    {/* Il nome squadra è già un link nell'intestazione della partita: qui è testo semplice,
        serve solo a orientare chi ha scrollato oltre, non a duplicare lo stesso link. */}
    <h2 className="partita-referto-titolo">{squadra}</h2>
    <TabellaOrdinabile
      colonne={colonneReferto}
      righe={giocatori || []}
      chiaveRiga={(g) => g.idGiocatore ?? `${g.cognome}-${g.numero}`}
      ordineIniziale={{ campo: 'mete', verso: 'desc' }}
      titoloCard={(g) => <LinkGiocatore id={g.idGiocatore} nome={g.nome} cognome={g.cognome} />}
      sottotitoloCard={(g) => `${g.maglia || squadra} · #${g.numero ?? '-'}`}
      vuoto="Nessuna statistica registrata"
    />
  </section>
);

/**
 * Il referto di una singola partita. Non carica niente e non ha un endpoint suo: i dati arrivano
 * dal layout dell'edizione, che li ha già scaricati una volta per tutte le sue viste. Chiederne
 * una seconda copia per un sottoinsieme di ciò che è già in mano significherebbe rifare su ogni
 * navigazione il download dell'intera edizione.
 */
export default function Partita() {
  const { idEdizione, idPartita } = useParams();
  const { statistiche } = useOutletContext();

  const partita = statistiche?.partite?.find((p) => String(p.idPartita) === String(idPartita));
  if (!partita) return <NonTrovato cosa="Questa partita" />;

  return (
    <div className="detail-view">
      <div className="detail-header">
        <Link to={urlEdizione(idEdizione)} className="back-button">
          <ArrowLeft size={20} />
          Torna all'edizione
        </Link>
      </div>

      <header className="partita-testata">
        <div className="partita-squadra"><LinkSquadra slug={slugDaId(statistiche, partita.idSquadra1)} nome={partita.squdra1} /></div>
        <div className="partita-punteggio"><span>{partita.mete1}</span><span>–</span><span>{partita.mete2}</span></div>
        <div className="partita-squadra"><LinkSquadra slug={slugDaId(statistiche, partita.idSquadra2)} nome={partita.squdra2} /></div>
      </header>

      <p className="partita-contesto">
        {partita.nome || partita.fase}
        {partita.data ? ` · ${new Date(partita.data).toLocaleDateString('it-IT')}` : ''}
      </p>

      <Referto squadra={partita.squdra1} giocatori={partita.squadra1Giocatori} />
      <Referto squadra={partita.squdra2} giocatori={partita.squadra2Giocatori} />
    </div>
  );
}
